/**
 * AgentAPIService.js
 * Handles API calls to backend Cloud Functions for agent execution
 */

import { getAuth } from "firebase/auth";

class AgentAPIService {
  constructor() {
    this.auth = getAuth();

    // Detect environment and set API base URL (using Express app pattern)
    this.apiBaseURL = window.location.hostname.includes("localhost")
      ? "http://127.0.0.1:5001/damia-gcp/us-central1/api"
      : "https://us-central1-damia-gcp.cloudfunctions.net/api";
  }

  async getIdToken() {
    const user = this.auth.currentUser;
    if (!user) throw new Error("No authenticated user");
    return await user.getIdToken();
  }

  async callAPI(endpoint, method = "POST", body = null) {
    const idToken = await this.getIdToken();
    const url = `${this.apiBaseURL}/${endpoint}`;

    const options = {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
    };

    if (body && method !== "GET") {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `API error: ${response.status}`);
    }

    return data;
  }

  // ==================== AGENT EXECUTION ====================

  async executeFlowchartAgent({ teamId, contextIds, prompt, options }) {
    // Route to different endpoint based on AI provider
    const endpoint =
      options.aiProvider === "openai"
        ? "executeFlowchartAgentOpenAI"
        : "executeFlowchartAgent";

    return await this.callAPI(endpoint, "POST", {
      teamId,
      contextIds,
      prompt,
      options,
    });
  }

  async refineFlowchart({ teamId, executionId, feedback, currentCode }) {
    return await this.callAPI("refineFlowchart", "POST", {
      teamId,
      executionId,
      feedback,
      currentCode,
    });
  }

  async validateMermaid({ mermaidCode }) {
    return await this.callAPI("validateMermaid", "POST", {
      mermaidCode,
    });
  }

  // ==================== DOCUMENT PROCESSING ====================

  async processDocument(teamId, contextId, downloadURL, mimeType, extension) {
    return await this.callAPI("processDocument", "POST", {
      teamId,
      contextId,
      downloadURL,
      mimeType,
      extension,
    });
  }

  // ==================== AGENT MANAGEMENT ====================

  async listAvailableAgents() {
    return await this.callAPI("listAgents", "GET");
  }

  async getAgentHistory(teamId, limit = 50) {
    return await this.callAPI("getAgentHistory", "GET", {
      teamId,
      limit,
    });
  }
}

export default AgentAPIService;
